<template>
    <ContentComp
        :load-state="LoadState"
        :show-as-modal="true"
    >
        <MapComp
            ref="map"
            :tracks="[maptrack]"
            :height="500"
            :centerpoint="centerPoint"
            :showmaplabels="true"
            :hide-progress="true"
            @ready="onReady"
        />
    </ContentComp>
</template>

<script setup lang="ts">
import { MapComp } from '@/components';
import ContentComp from '@/components/layout/ContentComp.vue';
import type { IMapExposed } from '@/components/Map/IMapExposed';
import { MapMarker } from '@/components/Map/MapMarker';
import { MapTrackData } from '@/components/Map/MapTrackData';
import { useTaskLoader } from '@/composeables';
import { MapPoint, TrackingDeviceWithRaceContestantPosition } from '@/objects/generated';
import { useContestantStore, useEventCommonStoreRef, useMapstore } from '@/stores';
import { getColorByString } from '@/utilities';
import { utcToLocalTime } from '@/utilities/dateutils';
import { computed, onMounted, ref } from 'vue';

const props = defineProps<{
    value: TrackingDeviceWithRaceContestantPosition | null;
}>();

/**
 * The map component
 */
const map = ref<IMapExposed | null>(null);

const { Races } = useEventCommonStoreRef();

const { GetContestantByRaceContestantId, GetDisplayName } = useContestantStore();

const mapstore = useMapstore();

const { AddLoader, LoadState } = useTaskLoader();

// This is the track we want to show on the map
const maptrack = ref<MapTrackData>(new MapTrackData());

// This is the center point of the map. Usually the first of the track points.
const centerPoint = ref<MapPoint | null>(null);

let refreshPromiseResolver: (() => void) | null = null;
let mockpromise = new Promise<void>((resolve) => {
    // When we resolve, we reset the promise and the resolver
    refreshPromiseResolver = () => {
        resolve();
        refreshPromiseResolver = null;
    };
});

const computedMarkers = computed(() => {
    // If we have a map, we can return the markers
    if (!map.value || centerPoint?.value == null || props.value?.RaceContestantID == null) {
        return []; // No markers if no map or center point
    }
    let gpspoint = null;
    if (props.value.RaceContestantPosition && props.value.RaceContestantPosition.GpsPoint) {
        gpspoint = props.value.RaceContestantPosition.GpsPoint;
    }

    let marker = new MapMarker();
    marker.Key = props.value?.RaceContestantID;
    marker.Type = 'circle';
    marker.Height = 15;
    marker.Width = 15;
    marker.Title = GetDisplayName(props.value.RaceContestantID, 'short');
    marker.Point = gpspoint ?? props.value.LastKnownPosition ?? null;
    marker.Color = getContestantColor(props.value.RaceContestantID).background;

    return [marker];
});

// Create a promise that is running
AddLoader(() => mockpromise, '');

const onReady = async () => {
    if (props.value == null) {
        console.warn('TrackingDevicePositionView: value is null');
        return;
    }

    // We need to find the current race contestant
    const contestant = GetContestantByRaceContestantId(props.value.RaceContestantID);
    const raceContestant = contestant?.RaceContestants.find((rc) => rc.ID === props.value!.RaceContestantID);

    if (!raceContestant) {
        console.warn('TrackingDevicePositionView: No raceContestant found for RaceContestantID', props.value.RaceContestantID);
        return;
    }

    // We need to find the race and the mapid for the race
    const trackId = Races.value.find((r) => r.ID === raceContestant.RaceID)?.MapID;

    if (!trackId) {
        console.warn('TrackingDevicePositionView: No trackId found for RaceContestantID', props.value.RaceContestantID);
        return;
    }

    const maptrackdata = await mapstore.GetMapTrackData(trackId);

    //Set the centerpoint
    if (maptrackdata && maptrackdata.MapTrack.Points.length > 0) {
        maptrack.value = maptrackdata;
        centerPoint.value = maptrack.value?.MapTrack.Points[0];
    }

    let color = getContestantColor(raceContestant.ID);

    let gpspoint = null;
    let time = null;

    if (props.value.RaceContestantPosition && props.value.RaceContestantPosition.GpsPoint) {
        gpspoint = props.value.RaceContestantPosition.GpsPoint;
        time = utcToLocalTime(gpspoint.UTCTime);
    }

    if (!gpspoint && props.value.LastKnownPosition) {
        gpspoint = props.value.LastKnownPosition;
        time = props.value.LastBatteryUpdate;
    }

    if (props.value.RaceContestantPosition?.EstimatedPoint) {
        let marker = new MapMarker();
        marker.Key = raceContestant.ID * -1; // Use negative ID for estimated position
        marker.Color = color.background;
        marker.Point = props.value.RaceContestantPosition?.GpsPoint;
        marker.Type = 'square';
        marker.Height = 10;
        marker.Width = 10;
        marker.StrokeColor = color.border;

        map.value?.AddMarker(marker);
    }

    if (gpspoint) {
        let marker = new MapMarker();
        marker.Key = raceContestant.ID;
        marker.Color = color.background;
        marker.Point = gpspoint;
        marker.Type = 'circle';
        marker.Title = GetDisplayName(raceContestant.ID, 'short') + '\r\n(' + time?.toLocaleString() + ')';
        marker.Height = 16;
        marker.Width = 16;
        marker.StrokeColor = color.border;

        // Update the center point to the gps point
        centerPoint.value = gpspoint;

        map.value?.AddMarker(marker);
    }

    if (refreshPromiseResolver != null) {
        refreshPromiseResolver();
    }

    map.value?.SetZoom('city');
};

onMounted(async () => {});

const getContestantColor = (raceContestantID: number) => {
    let colorObject = getColorByString(raceContestantID.toString(), 'dark', 90);

    return colorObject;
};
</script>
